import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import Animated, { withDelay, withSpring } from 'react-native-reanimated';

import { AnimatedNumber } from '@/components/AnimatedNumber';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useBetslipData, usePreviousBetslip } from '@/feature/betslip-pickem/hooks/use-betslip-data';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { useEntryRules } from '@/feature/betslip-pickem/hooks/use-entry-rules';
import { getWinningPicksAndToWin, hasMultiplierExceededError } from '@/feature/betslip-pickem/utils/betslip-utils';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import {
    getEnteringAnimation,
    getExitingAnimation,
    getLayoutAnimationsTransition,
} from '@/utils/animations/layout-animations-utils';

import { HEIGHT_WITHOUT_BORDER } from './FantasyPickbar';

const SHOW_MULTIPLIER_WITH_AMOUNT = 'showMultiplierWithAmount';
const SHOW_NOT_ENOUGH_PICKS = 'showNotEnoughPicks';
const SHOW_INVALID_MULTIPLIER = 'showInvalidMultiplier';

export const PickbarMultiplierInfo = ({ hasRendered }: { hasRendered: boolean }) => {
    const { dynamicMultipliers, dynamicBoostedMultiplier, perfectActiveMultiplier } = useBetslipData();
    const { picks } = usePreviousBetslip();
    const { highestMultiplier } = getWinningPicksAndToWin(dynamicMultipliers.DYNAMIC, dynamicBoostedMultiplier);
    const { t } = useTranslation(['betslip_pickem']);
    const validating = useBetslipStore(state => state.validating);
    const { minNumberOfPicks } = useEntryRules();
    const notEnoughPicks = picks.length < minNumberOfPicks;

    const gameMode = useBetslipStore(state => state.activeGameMode);
    const validationData = useBetslipStore(state => state.validationData[gameMode]);
    const isMaxMultiplierExceeded = hasMultiplierExceededError(validationData?.betValidation);
    const configurableAmount = useJurisdictionStore(
        store => store.jurisdictionSettings?.productConfig?.keyValuePairs?.['Pick Bar Amount']?.pick_bar_amount
    );
    const amount = configurableAmount || 100;
    const previousValuesRef = useRef({ maxMultiplier: 0, payoutAmount: 0 });
    const layoutTransition = getLayoutAnimationsTransition();

    // Memoize the multiplier values to prevent updates during validation
    const { maxMultiplier, payoutAmount } = useMemo(() => {
        if (validating || notEnoughPicks) {
            if (previousValuesRef.current.maxMultiplier === 0) {
                // if the pickbar just mounted (eg coming back to the lobby from pickslip)
                // we don't have previous values, so return the current ones
                const currentMaxMultiplier = perfectActiveMultiplier ?? highestMultiplier;
                const currentPayoutAmount = currentMaxMultiplier * Number(amount);

                return {
                    maxMultiplier: currentMaxMultiplier,
                    payoutAmount: currentPayoutAmount,
                };
            }
            return previousValuesRef.current;
        }
        const currentMaxMultiplier = perfectActiveMultiplier ?? highestMultiplier;
        const currentPayoutAmount = currentMaxMultiplier * Number(amount);

        // Store current values as previous values for next validation
        previousValuesRef.current = {
            maxMultiplier: currentMaxMultiplier,
            payoutAmount: currentPayoutAmount,
        };

        return {
            maxMultiplier: currentMaxMultiplier,
            payoutAmount: currentPayoutAmount,
        };
    }, [perfectActiveMultiplier, highestMultiplier, amount, validating, notEnoughPicks]);

    const renderInvalidMultiplier = () => {
        return (
            <Animated.View
                entering={getEnteringAnimation(slideInFromRightEntering, hasRendered)}
                exiting={getExitingAnimation(slideOutToRightExiting)}
                layout={layoutTransition}
                style={styles.multiplierContent}
            >
                <Text variant={'labelLarge2'} lineHeight={24}>
                    {t('invalid_multiplier')}
                </Text>
            </Animated.View>
        );
    };

    const renderNoPicks = () => {
        return (
            <Animated.View
                exiting={getExitingAnimation(slideOutToRightExiting)}
                entering={getEnteringAnimation(slideInFromRightEntering, hasRendered)}
                layout={layoutTransition}
                style={styles.multiplierContent}
            >
                <Text variant={'labelLarge2'} lineHeight={24}>
                    {t('no_pick_button', { count: minNumberOfPicks })}
                </Text>
            </Animated.View>
        );
    };

    const displayFormatMaxMultiplier = useCallback((multiplier: number) => {
        return `${multiplier}x`;
    }, []);

    const displayFormatPayoutAmount = useCallback((amt: number) => {
        return `$${Number(amt.toFixed(2))}`;
    }, []);

    const renderMultiplierWithAmount = () => {
        return (
            <Animated.View
                exiting={getExitingAnimation(slideOutToRightExiting)}
                entering={getEnteringAnimation(slideInFromRightEntering, hasRendered)}
                layout={layoutTransition}
                style={[styles.multiplierContent, styles.spaceBottom]}
            >
                <Animated.View style={styles.row} layout={layoutTransition}>
                    <Text variant={'labelLarge2'} fontWeight={'600'} letterSpacing={-0.23}>
                        {t('wins_up_to')}
                    </Text>
                    <AnimatedNumber
                        decimals={0}
                        value={maxMultiplier}
                        displayFormat={displayFormatMaxMultiplier}
                        color={ds.colorToken.text.brand}
                        variant={'labelLarge2'}
                        alignText={'flex-start'}
                        letterSpacing={-0.23}
                    />
                </Animated.View>
                <Animated.View style={[styles.row, styles.margin]} layout={layoutTransition}>
                    <Text variant={'bodySmaller'} color={'neutral300'} fontWeight={'500'} lineHeight={16}>
                        {`$${amount} ${t('pays')}`}
                    </Text>
                    <AnimatedNumber
                        decimals={0}
                        value={payoutAmount}
                        displayFormat={displayFormatPayoutAmount}
                        color={'neutral300'}
                        variant={'bodySmaller'}
                        alignText={'flex-start'}
                        lineHeight={16}
                        fontWeight={'500'}
                    />
                </Animated.View>
            </Animated.View>
        );
    };

    const hasEnoughPicks = picks.length >= minNumberOfPicks;
    // very rare case when picks exist but multiplier is 0 because it is not set in Retool
    const invalidMultiplier = hasEnoughPicks && maxMultiplier === 0;

    let status = SHOW_MULTIPLIER_WITH_AMOUNT;
    if (notEnoughPicks) {
        status = SHOW_NOT_ENOUGH_PICKS;
    } else if (isMaxMultiplierExceeded || invalidMultiplier) {
        status = SHOW_INVALID_MULTIPLIER;
    }

    return (
        <Animated.View layout={layoutTransition} testID={'pickbar-multiplier-info'}>
            {/* render all possible states like this because we want to use enter/exit animations */}
            {status === SHOW_NOT_ENOUGH_PICKS ? renderNoPicks() : null}
            {status === SHOW_MULTIPLIER_WITH_AMOUNT ? renderMultiplierWithAmount() : null}
            {status === SHOW_INVALID_MULTIPLIER ? renderInvalidMultiplier() : null}
        </Animated.View>
    );
};

const slideInFromRightEntering = () => {
    'worklet';
    const animations = {
        opacity: withDelay(
            50,
            withSpring(1, {
                damping: 20,
                stiffness: 300,
                mass: 0.5,
            })
        ),
        transform: [
            {
                translateX: withSpring(0, {
                    damping: 15,
                    stiffness: 200,
                    mass: 0.8,
                }),
            },
        ],
    };
    const initialValues = {
        opacity: 0,
        transform: [{ translateX: 25 }],
    };
    return {
        initialValues,
        animations,
    };
};

const slideOutToRightExiting = () => {
    'worklet';
    const animations = {
        opacity: 0,
        transform: [
            {
                translateX: withSpring(25, {
                    damping: 15,
                    stiffness: 150,
                    mass: 0.8,
                }),
            },
        ],
    };
    const initialValues = {
        opacity: 1,
        transform: [{ translateX: 0 }],
    };
    return {
        initialValues,
        animations,
    };
};

const styles = StyleSheet.create({
    multiplierContent: {
        height: HEIGHT_WITHOUT_BORDER,
        alignItems: 'flex-end',
        justifyContent: 'center',
        marginRight: 31,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    margin: {
        marginTop: 2,
    },
    spaceBottom: {
        marginBottom: 2,
    },
});
